/**
 * SiteContactsTable
 *
 * Read table of per-site contacts. When `canEdit` is true, renders a
 * "New Contact" button plus row-level edit/delete icons. When false,
 * renders a read-only table (matches site_viewer / analyst expectations).
 *
 * Issue: djin-cloud#64
 */

import { useState } from 'react';
import { Mail, Phone, Plus, Pencil, Trash2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteSiteContact, type SiteContact } from '../../services/adminApi';
import { SiteContactEditDrawer } from './SiteContactEditDrawer';

interface Props {
  siteId: string;
  contacts: SiteContact[];
  canEdit: boolean;
}

export function SiteContactsTable({ siteId, contacts, canEdit }: Props) {
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | null>(null);
  const [editingContact, setEditingContact] = useState<SiteContact | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (contactId: string) => deleteSiteContact(siteId, contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-contacts', siteId] });
      setDeleteError(null);
    },
    onError: (err: any) => {
      setDeleteError(err?.response?.data?.error ?? 'Failed to delete contact.');
    },
  });

  const handleNew = () => {
    setEditingContact(null);
    setDrawerMode('create');
  };

  const handleEdit = (c: SiteContact) => {
    setEditingContact(c);
    setDrawerMode('edit');
  };

  const handleDelete = (c: SiteContact) => {
    if (!confirm(`Delete contact "${c.name}"?`)) return;
    deleteMutation.mutate(c.contact_id);
  };

  return (
    <div className="bg-ems-bg-card rounded-2xl border border-ems-border">
      <div className="flex items-center justify-between px-4 py-3 border-b border-ems-border">
        <div>
          <h2 className="text-sm font-semibold text-ems-text-primary">Contacts</h2>
          <p className="text-xs text-ems-text-muted mt-0.5">
            {contacts.length === 0
              ? 'No contacts on file for this site.'
              : `${contacts.length} contact${contacts.length === 1 ? '' : 's'} on file.`}
          </p>
        </div>
        {canEdit && (
          <button
            onClick={handleNew}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-ems-green text-white text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> New Contact
          </button>
        )}
      </div>

      {deleteError && (
        <div className="px-4 py-2 text-sm text-ems-red bg-ems-red/5 border-b border-ems-red/20">
          {deleteError}
        </div>
      )}

      {contacts.length === 0 ? (
        <div className="p-6 text-sm text-ems-text-muted text-center">
          {canEdit
            ? 'Click "New Contact" to add operations, maintenance, or vendor escalation contacts.'
            : 'Ask a customer administrator to add contacts for this site.'}
        </div>
      ) : (
        <ul className="divide-y divide-ems-border">
          {contacts.map((c) => (
            <li key={c.contact_id} className="px-4 py-3 flex items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-sm font-medium text-ems-text-primary">{c.name}</span>
                  {c.role_title && (
                    <span className="text-xs text-ems-text-muted">{c.role_title}</span>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-4 flex-wrap text-xs text-ems-text-secondary">
                  {c.phone && (
                    <a
                      href={`tel:${c.phone}`}
                      className="inline-flex items-center gap-1 hover:text-ems-green"
                    >
                      <Phone className="w-3 h-3" /> {c.phone}
                    </a>
                  )}
                  {c.email && (
                    <a
                      href={`mailto:${c.email}`}
                      className="inline-flex items-center gap-1 hover:text-ems-green"
                    >
                      <Mail className="w-3 h-3" /> {c.email}
                    </a>
                  )}
                </div>
                {c.notes && (
                  <p className="mt-1 text-xs text-ems-text-muted whitespace-pre-wrap">
                    {c.notes}
                  </p>
                )}
              </div>
              {canEdit && (
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleEdit(c)}
                    className="p-2 rounded hover:bg-ems-bg-card-hover text-ems-text-muted hover:text-ems-text-primary"
                    aria-label={`Edit ${c.name}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(c)}
                    className="p-2 rounded hover:bg-ems-red/10 text-ems-text-muted hover:text-ems-red"
                    aria-label={`Delete ${c.name}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {drawerMode && (
        <SiteContactEditDrawer
          siteId={siteId}
          contact={editingContact}
          mode={drawerMode}
          onClose={() => setDrawerMode(null)}
        />
      )}
    </div>
  );
}
